// src/hooks/useDriverAuth.js
import { useSelector } from 'react-redux';
import { useCallback } from 'react';
import { useAuth } from './useAuth';
import { DRIVER_STATUS, VERIFICATION_STATUS } from '../store/driverSlice';

/**
 * Custom hook for driver data access for admin functionality
 * Provides driver record lookup and status information for admin components
 */
export const useDriverAuth = () => {
  const baseAuth = useAuth();
  const { drivers } = useSelector(state => state.drivers);

  const { user } = baseAuth;

  // Find the driver record for the current authenticated user (for admin display purposes)
  const driverRecord = drivers.find(driver =>
    driver.personalInfo.email === user?.email
  );

  // Driver status helpers (for admin display)
  const isVerifiedDriver = driverRecord?.status === DRIVER_STATUS.APPROVED &&
                          driverRecord?.verificationStatus === VERIFICATION_STATUS.APPROVED;
  const isActiveDriver = driverRecord?.status === DRIVER_STATUS.ACTIVE;
  const isPendingDriver = driverRecord?.status === DRIVER_STATUS.PENDING;
  const isRejectedDriver = driverRecord?.status === DRIVER_STATUS.REJECTED;
  const isSuspendedDriver = driverRecord?.status === DRIVER_STATUS.SUSPENDED;

  // Driver verification status helpers (for admin display)
  const needsDocuments = driverRecord?.verificationStatus === VERIFICATION_STATUS.DOCUMENTS_REQUIRED;
  const underReview = driverRecord?.verificationStatus === VERIFICATION_STATUS.UNDER_REVIEW;

  // Driver status helpers
  const getDriverStatusMessage = useCallback(() => {
    if (!driverRecord) return 'Driver record not found';
    
    switch (driverRecord.status) {
      case DRIVER_STATUS.PENDING:
        return 'Your application is being reviewed. You will be notified once approved.';
      case DRIVER_STATUS.APPROVED:
        return 'Your application has been approved! You can start accepting trips.';
      case DRIVER_STATUS.ACTIVE:
        return 'You are active and can accept trips.';
      case DRIVER_STATUS.INACTIVE:
        return 'Your account is inactive. Contact support to reactivate.';
      case DRIVER_STATUS.SUSPENDED:
        return 'Your account has been suspended. Contact support for more information.';
      case DRIVER_STATUS.REJECTED:
        return 'Your application was not approved. You can reapply or contact support.';
      default:
        return 'Unknown status';
    }
  }, [driverRecord]);

  const getVerificationStatusMessage = useCallback(() => {
    if (!driverRecord) return '';
    
    switch (driverRecord.verificationStatus) {
      case VERIFICATION_STATUS.PENDING:
        return 'Your documents are being reviewed.';
      case VERIFICATION_STATUS.DOCUMENTS_REQUIRED:
        return 'Additional documents are required. Please upload the requested documents.';
      case VERIFICATION_STATUS.UNDER_REVIEW:
        return 'Your application is under detailed review.';
      case VERIFICATION_STATUS.APPROVED:
        return 'Your verification is complete.';
      case VERIFICATION_STATUS.REJECTED:
        return 'Your verification was not approved. Please contact support.';
      default:
        return '';
    }
  }, [driverRecord]);



  // Driver statistics
  const driverStats = driverRecord ? {
    totalTrips: driverRecord.totalTrips || 0,
    totalEarnings: driverRecord.totalEarnings || 0,
    rating: driverRecord.rating || 0,
    completionRate: driverRecord.completionRate || 0,
    lastActive: driverRecord.lastActive
  } : null;

  return {
    // Base auth properties
    ...baseAuth,

    // Driver record for admin display
    driverRecord,

    // Driver status for admin display
    isVerifiedDriver,
    isActiveDriver,
    isPendingDriver,
    isRejectedDriver,
    isSuspendedDriver,

    // Driver status helpers for admin display
    needsDocuments,
    underReview,
    getDriverStatusMessage,
    getVerificationStatusMessage,

    // Driver statistics for admin display
    driverStats
  };
};




